import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { PasSekcji } from '@/components/PasSekcji';
import { Segmenty } from '@/components/Segmenty';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { NotaPrzyczynowosci } from '@/components/NotaPrzyczynowosci';
import { Karta } from '@/components/Karta';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { EmptyState, ErrorState, InsightOutcome, LoadingState } from '@/components/states';
import type { Dowod } from '@/components/SzufladaDowodu';
import type { InsightType } from '@/lib/api/analytics';
import { config, describeDataOrigin } from '@/config/env';
import { DEFAULT_THRESHOLDS } from '@/lib/crossSystem';
import { DATA_QUALITY_LABEL, worstQuality, type DataQuality } from '@/lib/dataQuality';
import { formatCount, formatRange } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useDateRange } from '@/lib/useDateRange';
import { useDishSales, useSalesDeliveryCoincidence } from '@/lib/useAnalytics';
import { KartaSygnalu } from './KartaSygnalu';
import { ANATOMIA, NAGLOWEK_KROKU, OPIS_KROKU } from './anatomia';
import { WiarygodnoscZlaczenia } from './WiarygodnoscZlaczenia';
import { ETYKIETA_TYPU, PODSTAWA_SILNIKA_DAN, SWIEZOSC_CRM, sygnalZeZbieznosci, sygnalZInsightu } from './sygnaly';

const GRID = { display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' } as const;

/*
 * ADRESY KART SYGNAŁÓW — po REGULE, nie po pozycji w tablicy.
 *
 * Karty sygnałów sprzedażowych powstają w pętli po `insights[]`, a inwentarz przypisuje osobny
 * identyfikator każdej REGULE (INSIGHTS-09 … INSIGHTS-15). Kuszące byłoby liczenie adresu z
 * indeksu pętli — i byłby to błąd, który ujawnia się dopiero na produkcji: silnik zwraca każdą
 * regułę TYLKO wtedy, gdy przekroczyła próg (crm/backend/lib/dishSalesAnalytics.js — każdy
 * `insights.push` jest pod własnym `if`). Gdy zabraknie TOP_DISH, na indeksie 0 siada „największy
 * spadek" i dostaje adres „największej wartości katalogowej”. Walidacja zameldowałaby wtedy
 * zgodność karty, której nie sprawdziła — czyli dokładnie ten fałsz, przed którym adresowanie
 * po atrybucie ma chronić.
 *
 * Klucz `type` jest jednoznaczny: silnik dokłada każdą regułę co najwyżej raz (sześć reguł w
 * `buildInsights` + doklejany osobno SCHOOL_OUTLIER), więc jeden adres = jedna karta w DOM.
 * Kolejność wpisów niżej odpowiada kolejności renderu i numeracji inwentarza.
 */
const ADRES_KARTY_SYGNALU: Record<InsightType, string> = {
  TOP_DISH: 'INSIGHTS-09',
  FASTEST_DECLINING_DISH: 'INSIGHTS-10',
  FASTEST_GROWING_DISH: 'INSIGHTS-11',
  PRICE_CHANGE_SIGNAL: 'INSIGHTS-12',
  LOW_PERFORMER: 'INSIGHTS-13',
  HIGH_REPEAT_PRODUCT: 'INSIGHTS-14',
  SCHOOL_OUTLIER: 'INSIGHTS-15',
};

/**
 * INSIGHTY — migracja do języka „SYGNAŁ" (zaakceptowany kierunek C, R5-15-009).
 *
 * PYTANIE EKRANU: co w wybranym oknie przekroczyło progi na tyle, żeby warto było o tym wiedzieć —
 * i czego z tego nadal nie wiadomo.
 *
 * TRZY DOPUSZCZALNE ODPOWIEDZI, KAŻDA NAZWANA WPROST:
 *   1. są sygnały                       → karty w stałej anatomii,
 *   2. przeanalizowano N, nic nie przeszło progów → „Brak istotnych sygnałów" (to jest WYNIK),
 *   3. nie było czego analizować        → „Brak danych do analizy" (to NIE jest wynik).
 * Druga i trzecia wyglądają na ekranie podobnie i znaczą coś przeciwnego. Zlanie ich w jeden
 * komunikat („nic się nie dzieje") jest najtańszym sposobem, żeby produkt zaczął kłamać —
 * dlatego rozróżnienie mieszka w components/states.tsx razem z testami, a nie w tym pliku.
 *
 * ŻADNYCH DANYCH DEMONSTRACYJNYCH. Ekran czyta wyłącznie realne API przez istniejące haki.
 * Puste źródło jest pokazywane jako pustka.
 */
export function InsightsPage() {
  const [range] = useDateRange();
  const dishes = useDishSales();
  const cross = useSalesDeliveryCoincidence();

  const dane = dishes.data;
  const crossReady = cross.data?.available === true ? cross.data : null;
  const crossNiedostepne = cross.data?.available === false ? cross.data : null;

  const oknoLabel = formatRange(range.date_from, range.date_to);
  /*
   * Okno porównawcze bierzemy z ODPOWIEDZI silnika, a nie liczymy go tutaj po raz drugi.
   * Front, który sam wyznacza okno porównawcze, prędzej czy później pokaże inne okno niż to,
   * na którym backend policzył wynik — i nikt tego nie zauważy, bo obie liczby wyglądają poprawnie.
   * Dopóki odpowiedzi nie ma, okno jest po prostu NIEZNANE.
   */
  const porownanieLabel = dane
    ? formatRange(dane.range.comparison.date_from, dane.range.comparison.date_to)
    : null;

  const insighty = dane?.insights ?? [];
  const daniaPrzeanalizowane = dane?.dishes.length ?? null;
  const pozycjeWOknie = dane?.summary.total_units ?? null;

  /*
   * ZERO TO NIE TO SAMO CO PUSTO — także dla LICZBY SYGNAŁÓW.
   * `insights.length === 0` znaczy dwie zupełnie różne rzeczy: „silnik zbadał dania i żadne nie
   * przekroczyło progu" albo „silnik nie dostał ani jednej pozycji do zbadania". Pierwsze jest
   * wynikiem, drugie stanem integracji. Rozstrzyga liczba wierszy w źródle, nie wartość wyniku.
   */
  const ziarno = { wierszy: pozycjeWOknie, bytow: daniaPrzeanalizowane };
  const zrodloPuste = czyPustyZbior(ziarno);
  const liczbaSygnalow = wartoscDoPokazania(dane ? insighty.length : null, ziarno);

  /*
   * ZBIEŻNOŚCI: brak policzonego złączenia to NIE „zero zbieżności".
   * Gdy złączenie jest ZABLOKOWANE (albo Drive niepodłączony), `findCoincidences` zwraca pustą
   * listę — dokładnie tak samo, jak gdy złączenie się udało i nic nie przekroczyło progów.
   * Pokazanie tam zera zamieniłoby awarię integracji w uspokajający wynik.
   */
  const liczbaZbieznosci = crossReady && crossReady.join.status !== 'BLOCKED' ? crossReady.coincidences.length : null;

  const rodzajeSygnalow = Array.from(new Set(insighty.map((i) => ETYKIETA_TYPU[i.type])));

  /*
   * PODZIAŁ NA ORZEKALNE I „BEZ ODPOWIEDZI" — po skali, która ma definicję.
   *
   * `lib/dataQuality.ts` zna sześć stanów. Trzy z nich mówią wprost, że próba nie pozwala
   * orzekać: mała próba, brak historii, niepewne łączenie. `UNKNOWN` dokładamy do tej samej
   * grupy, bo „jakość nieznana" to też brak podstawy do wniosku — nie jest to natomiast
   * to samo co „dane pełne" i nie wolno go tam wliczyć.
   */
  const NIEORZEKALNE = new Set<DataQuality>(['LOW_SAMPLE', 'MISSING_HISTORY', 'UNRELIABLE_JOIN', 'UNKNOWN']);
  const bezOdpowiedzi = insighty.filter((i) => NIEORZEKALNE.has(i.data_quality));
  const orzekalne = insighty.filter((i) => !NIEORZEKALNE.has(i.data_quality));

  const [filtrJakosci, setFiltrJakosci] = useState<'wszystkie' | 'orzekalne' | 'bezOdpowiedzi'>('wszystkie');
  const widoczneSygnaly =
    filtrJakosci === 'orzekalne' ? orzekalne : filtrJakosci === 'bezOdpowiedzi' ? bezOdpowiedzi : insighty;

  /*
   * Dowód liczby-bohatera powstaje z TYCH SAMYCH pól, które ją zasilają (summary, dishes, insights).
   * Warstwa prezentacji nie jest właścicielem liczb — gdyby dowód był pisany osobno, rozjechałby się
   * z wynikiem przy pierwszej zmianie kontraktu i nie wiadomo byłoby, które z dwojga kłamie.
   */
  const dowodLiczbySygnalow: Dowod = {
    tytul: 'Sygnały sprzedażowe w oknie',
    etykietaWartosci: 'Liczba sygnałów ponad progami',
    wartosc: liczbaSygnalow === null ? null : formatCount(liczbaSygnalow),
    podstawa: PODSTAWA_SILNIKA_DAN,
    zrodlo: dane
      ? `CRM /analytics/dish-sales → silnik reguł ${dane.engine_version}`
      : 'CRM /analytics/dish-sales → silnik reguł',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC_CRM,
    oknoBiezace: oknoLabel,
    oknoPorownawcze: porownanieLabel,
    wyliczenie:
      'Liczba obserwacji, które silnik reguł zwrócił dla tego okna. Silnik zestawia okno bieżące z równie długim oknem ' +
      'bezpośrednio poprzedzającym i oddaje wyłącznie te, które przekroczyły jego progi istotności. To są reguły ' +
      'deterministyczne na jawnych danych — nie ma tu modelu językowego ani oceny uznaniowej.',
    szczegoly: [
      ['Dania przeanalizowane', formatCount(daniaPrzeanalizowane)],
      ['Pozycji zamówień w oknie', formatCount(pozycjeWOknie)],
      ['Reguły obecne w wyniku', rodzajeSygnalow.length > 0 ? rodzajeSygnalow.join(' · ') : 'żadna'],
    ],
    proba: `${formatCount(pozycjeWOknie)} pozycji zamówień w ${formatCount(daniaPrzeanalizowane)} daniach`,
    jakoscDanych:
      insighty.length > 0 ? DATA_QUALITY_LABEL[worstQuality(insighty.map((i) => i.data_quality))] : undefined,
    ograniczenia: [
      ...(zrodloPuste
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero pozycji zamówień i zero dań w tym oknie. To NIE jest zmierzone zero — ' +
              'nie było czego badać. Sprzedaż mogła być; nie ma jej w tym źródle.',
          ]
        : []),
      'Zbieżność sprzedaż ↔ dostawy NIE wchodzi do tej liczby. Jest liczona osobnym silnikiem, na złączeniu dwóch ' +
        'systemów, i ma własną sekcję niżej — sumowanie obu dałoby liczbę bez definicji.',
      'Metryki „zbieżności ze sprzedażą" i „szkół dopasowanych" pokazane obok tej liczby pochodzą właśnie z tamtego ' +
        'silnika. Ich wyprowadzenie otwiera przycisk „Skąd te liczby" przy wiarygodności złączenia.',
      'Jedno danie może pojawić się w kilku regułach naraz, więc liczba sygnałów nie jest liczbą spraw do załatwienia.',
      ...(dane?.caveats ?? []),
    ],
    czegoNieWynika:
      'Ta liczba NIE mówi, ile problemów jest w sieci — mówi, ile obserwacji przekroczyło progi TEGO silnika na TYCH danych. ' +
      'Zero sygnałów nie znaczy „wszystko w porządku": reguła poniżej progu nie znika z rzeczywistości, tylko z ekranu. ' +
      'W drugą stronę też nie: pięć sygnałów może dotyczyć jednego dania widzianego z pięciu stron.',
  };

  const progiOpis =
    `spadek sprzedaży co najmniej ${DEFAULT_THRESHOLDS.minSalesDropPct}%, pogorszenie punktualności co najmniej ` +
    `${DEFAULT_THRESHOLDS.minOnTimeDropPoints} p.p., przy co najmniej ${DEFAULT_THRESHOLDS.minOrders} zamówieniach ` +
    `i ${DEFAULT_THRESHOLDS.minDeliveries} dostawach w oknie`;

  const powodBrakuZlaczenia = cross.isLoading
    ? 'Złączenie jest w trakcie liczenia — obie strony muszą przyjść, zanim będzie co oceniać.'
    : cross.error
      ? 'Nie udało się pobrać jednej ze stron złączenia. Powód jest wypisany wyżej; do czasu jego usunięcia żadna liczba ' +
        'zbieżności nie powstaje.'
      : crossNiedostepne
        ? 'Wymaga podłączenia systemu kierowców. Bez sesji Drive nie ma drugiej strony złączenia — nie ma czego dopasowywać ' +
          'ani czego oceniać. To jest widoczny koszt braku zunifikowanej tożsamości, nie usterka tego ekranu.'
        : 'Złączenia nie policzono.';

  return (
    <Box>
      <NaglowekEkranu
        tytul="Insighty"
        opis="Na jakie pytanie odpowiada ten ekran: co w wybranym oknie przekroczyło progi na tyle, żeby warto było o tym wiedzieć — i czego z tego nadal nie wiadomo."
        akcja={<DateRangeFilter wariant="naglowek" comparison={dane?.range.comparison} />}
      />

      {/*
        NIE UDAJEMY AI. To zdanie jest tu celowo, na widoku, a nie w dokumentacji: „insight" w
        produktach analitycznych domyślnie kojarzy się z modelem, który coś „zauważył". Tutaj nic
        niczego nie zauważa — progi są jawne, reguły deterministyczne, a wynik powtarzalny.
      */}
      <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', maxWidth: '72ch', mt: 1, mb: 2 }}>
        Bez modelu językowego i bez oceny AI: to reguły progowe policzone na jawnych danych z API. To samo okno i ten sam
        zbiór zawsze dadzą ten sam wynik, a każdy sygnał da się rozłożyć na liczby, z których powstał.{' '}
        {describeDataOrigin(config.dataOrigin)}.
      </Typography>

      {/*
        NOTA RZĄDZI CAŁYM EKRANEM, więc stoi NAD wszystkim, co opisuje — tak jak w makiecie.
        Produkt miał to zastrzeżenie wyłącznie na dole KAŻDEJ karty: poprawne, ale widoczne
        dopiero wtedy, gdy ktoś dojedzie do końca konkretnego sygnału. Czytelnik, który
        przewinie listę i wyciągnie wniosek o przyczynie, nie przeczyta go w ogóle.
        Zastrzeżenie per karta ZOSTAJE — to dwie różne role, nie duplikat.
      */}
      <NotaPrzyczynowosci tytul="Sygnał mówi CO, nigdy DLACZEGO" kartaId="INSIGHTS-21">
        Reguła widzi, że wielkość zmieniła się w tym oknie. Nie widzi, czy coś tę zmianę wywołało,
        ani czy obie obserwacje nie wynikają z trzeciej, której w ogóle nie mierzymy. Żadne zdanie
        na tym ekranie nie orzeka o przyczynie, a „Co sprawdzić" to lista PYTAŃ — nie lista
        winnych i nie lista przyczyn.
      </NotaPrzyczynowosci>

      <PasSekcji wariant="cichy" tytul="Jak powstaje sygnał" />

      {/*
        WYJAŚNIENIE ANATOMII. Karta sygnału anatomię EGZEKWUJE (stała kolejność czterech części
        w `KartaSygnalu`), ale nigdy nie mówiła czytelnikowi, po co ona jest. Lista pochodzi
        z tego samego pliku co kolejność w karcie — dwie kopie rozjechałyby się przy pierwszej
        zmianie nazwy i ekran tłumaczyłby budowę, której już nie ma.
      */}
      <Karta
        kartaId="INSIGHTS-22"
        nadtytul="Ta sama budowa w każdej pozycji"
        tytul="Cztery części każdego sygnału"
      >
        <Box component="ol" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
          {ANATOMIA.map((krok, i) => (
            <Box component="li" key={krok} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
              <Typography
                aria-hidden
                className="sygnal-liczba"
                sx={{ fontFamily: 'var(--krój-display)', color: 'var(--zloto)', minWidth: '2ch' }}
              >
                {i + 1}
              </Typography>
              <Box>
                <Typography component="h4" variant="overline" sx={{ display: 'block', color: 'var(--tekst)' }}>
                  {NAGLOWEK_KROKU[krok]}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--tekst-2)', maxWidth: '72ch' }}>
                  {OPIS_KROKU[krok]}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Karta>

      {dishes.isLoading ? <LoadingState /> : null}
      {dishes.error ? (
        <ErrorState
          error={dishes.error}
          context="Control Center czyta wyłącznie API. Gdy silnik nie odpowiada, nie pokazujemy sygnałów zastępczych — brak odpowiedzi to nie brak sygnałów."
        />
      ) : null}

      <Stack spacing={4} className="sygnal-kaskada">
        {dane ? (
          <PlytaGlowna
            kartaId="INSIGHTS-01"
            dowodId="INSIGHTS-06"
            etykieta="Sygnały sprzedażowe w tym oknie"
            wartosc={liczbaSygnalow === null ? null : formatCount(liczbaSygnalow)}
            opis={
              <Typography variant="body2">
                {zrodloPuste
                  ? 'źródło nie zwróciło ani jednej pozycji zamówienia — nie było czego badać, więc to nie jest zmierzone zero'
                  : `silnik reguł zbadał ${formatCount(daniaPrzeanalizowane)} dań i zwrócił tylko to, co przekroczyło progi istotności`}
              </Typography>
            }
            zrodlo={`CRM /analytics/dish-sales → silnik reguł ${dane.engine_version}`}
            okno={oknoLabel}
            dowod={dowodLiczbySygnalow}
            boczne={
              <>
                <MiniMetryka kartaId="INSIGHTS-02" wartosc={formatCount(daniaPrzeanalizowane)} etykieta="dań zbadanych" />
                <MiniMetryka kartaId="INSIGHTS-03" wartosc={formatCount(pozycjeWOknie)} etykieta="pozycji zamówień" />
                {/* Kreska, nie zero: bez policzonego złączenia liczba zbieżności nie istnieje. */}
                <MiniMetryka
                  kartaId="INSIGHTS-04"
                  wartosc={liczbaZbieznosci === null ? null : formatCount(liczbaZbieznosci)}
                  etykieta="zbieżności ze sprzedażą"
                />
                <MiniMetryka
                  kartaId="INSIGHTS-05"
                  wartosc={crossReady ? formatCount(crossReady.join.matched) : null}
                  etykieta="szkół dopasowanych"
                />
              </>
            }
          />
        ) : null}

        <PasSekcji
          tytul="Sprzedaż"
          opis={
            porownanieLabel
              ? `Reguły progowe na daniach: okno ${oknoLabel} zestawione z oknem ${porownanieLabel}.`
              : `Reguły progowe na daniach: okno ${oknoLabel}. Okno porównawcze poda silnik razem z wynikiem.`
          }
        />

        {/*
          FILTR SYGNAŁÓW — odpowiednik „Wagi" z makiety, ale po INNEJ OSI, i to jest świadome.

          Makieta filtruje po wadze: krytyczna / uwaga / sukces / bez odpowiedzi. Trzy pierwsze
          są OCENĄ, której ten produkt nie ma i nie może zmyślić — silnik reguł nie zwraca ani
          istotności, ani kierunku „dobry/zły", a wyprowadzenie ich z typu reguły byłoby
          wskaźnikiem pewności bez definicji. Dokładnie to, czego `KartaSygnalu` zabrania
          w swoim nagłówku: „żadnego wskaźnika pewności".

          Została oś, którą źródło NAPRAWDĘ niesie i która ma definicję w `lib/dataQuality.ts`:
          jakość danych. Zachowuje przy tym najcenniejszy kubełek makiety — „bez odpowiedzi",
          czyli sygnały, przy których próba nie pozwala orzekać. System mówi „nie wiem"
          zamiast zgadywać.
        */}
        {dane && insighty.length > 0 ? (
          <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
              Pokaż
            </Typography>
            <Segmenty
              etykieta="Filtr sygnałów wg jakości danych"
              wybrana={filtrJakosci}
              onZmiana={(w) => setFiltrJakosci(w)}
              opcje={[
                { wartosc: 'wszystkie' as const, etykieta: `Wszystkie (${insighty.length})` },
                { wartosc: 'orzekalne' as const, etykieta: `Orzekalne (${orzekalne.length})` },
                { wartosc: 'bezOdpowiedzi' as const, etykieta: `Bez odpowiedzi (${bezOdpowiedzi.length})` },
              ]}
            />
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', maxWidth: '58ch' }}>
              „Bez odpowiedzi" to sygnały, przy których jakość danych nie pozwala orzekać: mała
              próba, brak historii albo niepewne łączenie. Reguła je zwróciła — nie znaczy to,
              że są nieprawdziwe, znaczy, że nie wiadomo.
            </Typography>
          </Stack>
        ) : null}

        <Box component="section">

          {dane ? (
            insighty.length > 0 ? (
              widoczneSygnaly.length > 0 ? (
              <Box sx={GRID}>
                {widoczneSygnaly.map((insight) => (
                  <KartaSygnalu
                    key={insight.insight_id}
                    kartaId={ADRES_KARTY_SYGNALU[insight.type]}
                    sygnal={sygnalZInsightu(insight, dane.caveats)}
                  />
                ))}
              </Box>
              ) : (
                /* Filtr zawęził do zera. To NIE znaczy „nic się nie dzieje" — znaczy „nic
                   o tej jakości". Zlanie tych dwóch komunikatów jest najtańszym sposobem,
                   żeby ekran zaczął kłamać. */
                <EmptyState
                  title="Żaden sygnał nie ma tej jakości danych"
                  description={`Filtr zawęził listę do zera pozycji z ${formatCount(insighty.length)} zwróconych przez silnik. To wynik filtra, nie stan analizy — zdejmij zawężenie, żeby zobaczyć wszystkie.`}
                />
              )
            ) : (
              // Rozróżnienie „nie ma sygnału" od „nie ma danych" — patrz InsightOutcome.
              // Wcześniej oba przypadki dostawały ten sam komunikat, co przy pustym CRM czytało się
              // jako „sprawdziliśmy, jest spokojnie", choć nie sprawdzono niczego.
              <InsightOutcome
                kartaId="INSIGHTS-16"
                analysed={dane.dishes.length}
                window={{ date_from: dane.range.date_from, date_to: dane.range.date_to }}
                subject="dań"
              />
            )
          ) : null}
        </Box>

        <PasSekcji tytul="Sprzedaż ↔ dostawy" />

        <Box component="section">
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }} useFlexGap>
            <Box sx={{ flexGrow: 1 }} />
            {/*
              Odnośnik do ekranu pogłębionego. Nazwa przycisku świadomie NIE brzmi „Powiązania":
              tak samo nazywa się pozycja nawigacji, a dwa odnośniki o tej samej nazwie na jednym
              ekranie czynią wskazanie „kliknij Powiązania" niejednoznacznym — dla człowieka i dla
              testu, który tym kliknięciem sprawdza tamten ekran.
            */}
            <Button size="small" component={Link} to={`/insights/powiazania?from=${range.date_from}&to=${range.date_to}`}>
              pełna analiza dwóch systemów →
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', mb: 2 }}>
            Jedyne miejsce, w którym zestawiamy dwa systemy o rozłącznych kontach. Progi: {progiOpis}.
          </Typography>

          {cross.isLoading ? <LoadingState label="Łączenie danych sprzedażowych z dostawami…" /> : null}
          {cross.error ? <ErrorState error={cross.error} /> : null}

          {/*
            Raport wiarygodności złączenia stoi OBOK wyniku i renderuje się ZAWSZE — także (a zwłaszcza)
            wtedy, gdy złączenia nie da się policzyć. Ukryty rozjazd identyfikatorów jest gorszy niż
            brak analizy: pusty ekran wygląda wtedy identycznie jak spokojna sieć.
          */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, alignItems: 'start' }}>
            <Box>
              {crossNiedostepne ? (
                <Alert severity="info">
                  {crossNiedostepne.reason} Analiza łączona wymaga jednoczesnego dostępu do obu systemów — to jest
                  widoczny koszt braku zunifikowanej tożsamości, a nie brak sygnałów.
                </Alert>
              ) : null}

              {crossReady ? (
                crossReady.join.status === 'BLOCKED' ? (
                  <Alert severity="warning" data-cc-card-id="INSIGHTS-19">
                    Nie da się policzyć zbieżności: {crossReady.join.reason} Dopóki to trwa, liczba zbieżności nie wynosi
                    zero — po prostu nie istnieje.
                  </Alert>
                ) : crossReady.coincidences.length === 0 ? (
                  <Alert severity="success" data-cc-card-id="INSIGHTS-20">
                    Żadna szkoła nie pogorszyła jednocześnie sprzedaży i punktualności ponad progi istotności. To jest
                    wynik, nie brak danych.
                    {crossReady.join.status === 'PARTIAL'
                      ? ` Sprawdzono wyłącznie dopasowaną część sieci: ${crossReady.join.reason}`
                      : ''}
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {/*
                      JEDEN ADRES NA RODZAJ KARTY, nie na szkołę. Inwentarz opisuje to jako jedną
                      kartę „jedna na szkołę" (INSIGHTS-21) — liczba szkół jest zmienna i zależy od
                      danych, więc numerowanie z indeksu dawałoby adresy, których w inwentarzu nie
                      ma, i chwiałoby się przy każdym oknie. Tak samo zaadresowane są karty szkół
                      na ekranie Powiązania (CROSS_SYSTEM-18…21).
                    */}
                    {crossReady.coincidences.map((item) => (
                      <KartaSygnalu
                        key={item.school_id}
                        kartaId="INSIGHTS-21"
                        sygnal={sygnalZeZbieznosci(item, crossReady.window, crossReady.comparison)}
                      />
                    ))}
                  </Stack>
                )
              ) : null}
            </Box>

            <WiarygodnoscZlaczenia
              kartaId="INSIGHTS-22"
              report={crossReady ? crossReady.join : null}
              powodBraku={powodBrakuZlaczenia}
              okno={crossReady ? formatRange(crossReady.window.date_from, crossReady.window.date_to) : oknoLabel}
              porownanie={
                crossReady ? formatRange(crossReady.comparison.date_from, crossReady.comparison.date_to) : 'nie policzono'
              }
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
